import * as THREE from 'three'

/**
 * Mountain backdrop — a cylindrical panorama with a natural-looking
 * mountain ridge silhouette. Multiple layers for depth:
 * 1. Far ridge (light gray, smooth) — distant mountains
 * 2. Mid ridge (medium gray, jagged) — closer peaks with snow
 * 3. Near ridge (darker, more detail) — foothills
 */

function noise(x: number): number {
  // Smooth noise function using multiple sine waves
  return Math.sin(x * 1.0) * 0.4
    + Math.sin(x * 2.3 + 1.2) * 0.25
    + Math.sin(x * 4.1 + 2.8) * 0.15
    + Math.sin(x * 7.7 + 0.5) * 0.1
    + Math.sin(x * 13.2 + 3.7) * 0.06
    + Math.sin(x * 21.0 + 1.1) * 0.04
}

function createRidge(
  radius: number,
  segments: number,
  height: number,
  peakScale: number,
  color: number,
  snowColor: number | null,
  snowLine: number,  // fraction of height where snow starts (0-1)
  noiseOffset: number,
): THREE.Group {
  const ridge = new THREE.Group()

  // Main rock wall
  const geo = new THREE.CylinderGeometry(radius, radius, height, segments, 8, true)
  const positions = geo.attributes.position

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i)
    const y = positions.getY(i)
    const z = positions.getZ(i)
    const angle = Math.atan2(x, z)

    // Top vertices get the mountain ridge shape
    const normalizedY = (y + height / 2) / height  // 0 at bottom, 1 at top
    if (normalizedY > 0.5) {
      const ridgeHeight = noise(angle * 3 + noiseOffset) * peakScale
      const blend = (normalizedY - 0.5) * 2  // 0 to 1 for top half
      positions.setY(i, y + ridgeHeight * blend * blend)
    }
  }

  geo.computeVertexNormals()

  // Vertex colors for gradient: darker at base, lighter near peaks
  const colors = new Float32Array(positions.count * 3)
  const baseColor = new THREE.Color(color)
  const topColor = new THREE.Color(color).lerp(new THREE.Color(0xb0bcc8), 0.3)

  for (let i = 0; i < positions.count; i++) {
    const y = positions.getY(i)
    const normalizedY = (y + height / 2) / height
    const c = baseColor.clone().lerp(topColor, normalizedY)
    colors[i * 3] = c.r
    colors[i * 3 + 1] = c.g
    colors[i * 3 + 2] = c.b
  }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.85,
    side: THREE.BackSide,
  })
  const wall = new THREE.Mesh(geo, mat)
  ridge.add(wall)

  // Snow layer on top
  if (snowColor !== null) {
    const snowGeo = new THREE.CylinderGeometry(radius - 1, radius - 1, height * (1 - snowLine), segments, 4, true)
    const snowPositions = snowGeo.attributes.position

    for (let i = 0; i < snowPositions.count; i++) {
      const x = snowPositions.getX(i)
      const y = snowPositions.getY(i)
      const z = snowPositions.getZ(i)
      const angle = Math.atan2(x, z)
      const snowHeight = height * (1 - snowLine)
      const normalizedY = (y + snowHeight / 2) / snowHeight

      if (normalizedY > 0.4) {
        const ridgeHeight = noise(angle * 3 + noiseOffset) * peakScale
        const blend = (normalizedY - 0.4) * 1.67
        snowPositions.setY(i, y + ridgeHeight * blend * blend)
      }
    }

    snowGeo.computeVertexNormals()

    const snowMat = new THREE.MeshStandardMaterial({
      color: snowColor,
      roughness: 0.6,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.9,
    })
    const snowWall = new THREE.Mesh(snowGeo, snowMat)
    snowWall.position.y = height * snowLine * 0.5
    ridge.add(snowWall)
  }

  return ridge
}

export function createMountainBackdrop(): THREE.Group {
  const mountains = new THREE.Group()
  const segments = 128  // high segment count eliminates visible seam

  // Layer 1: Far distant mountains (lightest, smoothest, biggest)
  const far = createRidge(550, segments, 300, 60, 0x9eaab6, 0xe4eaef, 0.6, 0)
  far.position.y = -10
  mountains.add(far)

  // Layer 2: Mid mountains (medium tone, more jagged, snow caps)
  const mid = createRidge(480, segments, 250, 80, 0x7a8a98, 0xdce3ea, 0.55, 5)
  mid.position.y = -20
  mountains.add(mid)

  // Layer 3: Near foothills (darkest, most detail, closest)
  const near = createRidge(420, segments, 180, 50, 0x6a7886, null, 1, 10)
  near.position.y = -30
  mountains.add(near)

  // Rotate so the cylinder seam is behind the skier (uphill, not visible)
  mountains.rotation.y = Math.PI

  return mountains
}
